import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ProofReceipt } from '../types/governance';
import { HIPAA_SESSION_TIMEOUT_MINUTES } from '../../../shared/constants';
import { clearExecutionToken } from '../lib/trpc';

interface ApprovalRequest {
  capability: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  actor: string;
  description: string;
  reason?: string;
}

interface GovernanceContextType {
  showApprovalModal: (request: ApprovalRequest, onApprove: () => Promise<void>) => void;
  recordProof: (proof: ProofReceipt) => void;
  latestProof: ProofReceipt | null;
  clearProof: () => void;
  isAwaitingApproval: boolean;
}

const GovernanceContext = createContext<GovernanceContextType | undefined>(undefined);

export function GovernanceProvider({ children }: { children: React.ReactNode }) {
  const idleTimeoutMs = HIPAA_SESSION_TIMEOUT_MINUTES * 60 * 1000;
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<ApprovalRequest | null>(null);
  const [onApproveCallback, setOnApproveCallback] = useState<(() => Promise<void>) | null>(null);
  const [latestProof, setLatestProof] = useState<ProofReceipt | null>(null);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showApprovalModal = useCallback(
    (request: ApprovalRequest, onApprove: () => Promise<void>) => {
      setPendingRequest(request);
      setOnApproveCallback(() => onApprove);
      setIsAwaitingApproval(true);
    },
    [],
  );

  const recordProof = useCallback((proof: ProofReceipt) => {
    setLatestProof(proof);
    setTimeout(() => setLatestProof(null), 15000);
  }, []);

  const clearProof = useCallback(() => {
    setLatestProof(null);
  }, []);

  const clearGovernanceSessionState = useCallback(() => {
    setIsAwaitingApproval(false);
    setPendingRequest(null);
    setOnApproveCallback(null);
    setLatestProof(null);
    clearExecutionToken();
  }, []);

  const handleApprove = async () => {
    if (onApproveCallback) {
      await onApproveCallback();
      setIsAwaitingApproval(false);
      setPendingRequest(null);
      setOnApproveCallback(null);
    }
  };

  const handleDeny = () => {
    setIsAwaitingApproval(false);
    setPendingRequest(null);
    setOnApproveCallback(null);
  };

  useEffect(() => {
    const resetIdleTimeout = () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        clearGovernanceSessionState();
      }, idleTimeoutMs);
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimeout, { passive: true });
    });

    resetIdleTimeout();

    return () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimeout);
      });
    };
  }, [clearGovernanceSessionState, idleTimeoutMs]);

  return (
    <GovernanceContext.Provider
      value={{
        showApprovalModal,
        recordProof,
        latestProof,
        clearProof,
        isAwaitingApproval,
      }}
    >
      {children}
      {isAwaitingApproval && pendingRequest && (
        <ApprovalModal
          request={pendingRequest}
          onApprove={handleApprove}
          onDeny={handleDeny}
        />
      )}
    </GovernanceContext.Provider>
  );
}

export function useGovernance() {
  const context = useContext(GovernanceContext);
  if (!context) {
    throw new Error('useGovernance must be used within GovernanceProvider');
  }
  return context;
}

function ApprovalModal({
  request,
  onApprove,
  onDeny,
}: {
  request: ApprovalRequest;
  onApprove: () => void;
  onDeny: () => void;
}) {
  const riskColors = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-green-600',
  };

  const riskBgColors = {
    critical: 'bg-red-100',
    high: 'bg-orange-100',
    medium: 'bg-yellow-100',
    low: 'bg-green-100',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface border border-border rounded-lg shadow-lg max-w-md p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-text">Governance Approval Required</h2>
          <p className="text-text-secondary text-sm mt-1">
            This action requires approval before it can proceed.
          </p>
        </div>

        <div className="space-y-3 bg-background p-4 rounded-md">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Risk Level:</span>
            <span className={`font-medium ${riskColors[request.riskLevel]}`}>
              {request.riskLevel.charAt(0).toUpperCase() + request.riskLevel.slice(1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Capability:</span>
            <span className="text-sm font-mono text-text">{request.capability}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Actor:</span>
            <span className="text-sm text-text">{request.actor}</span>
          </div>
        </div>

        <div>
          <p className="text-sm text-text font-medium mb-2">Description:</p>
          <p className="text-sm text-text-secondary">{request.description}</p>
        </div>

        {request.reason && (
          <div>
            <p className="text-sm text-text font-medium mb-2">Reason:</p>
            <p className="text-sm text-text-secondary">{request.reason}</p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <button
            onClick={onDeny}
            className="flex-1 px-4 py-2 border border-border rounded-md text-text hover:bg-background transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onApprove}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-md font-medium hover:bg-primary-dark transition-colors"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
